/**
 * EF-CREW-ONBOARDING-FRONTEND-INTEGRATION-V1: Phase 5
 * 
 * Profile Photo Crop Modal
 * - Zoom, rotate, and adjust crop area
 * - Overlay detected face bounding box
 * - Save generates 400x400 profile and 100x100 thumbnail
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Slider } from "../ui/slider";
import { Label } from "../ui/label";
import {
  Camera,
  ZoomIn,
  ZoomOut,
  RotateCw,
  RotateCcw,
  Loader2,
  Check,
  X,
  Move,
  Square,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProfilePhotoCropModal({
  open,
  onOpenChange,
  imageUrl,
  staffId,
  assetId,
  suggestedCrop = null,
  allFaces = [],
  onSaveComplete,
}) {
  // State
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
  // cropRegion is DERIVED from containerSize — a centered square frame in
  // canvas-pixel space, 60% of min(width,height). It is intentionally NOT
  // state: zoom changes only the underlying image transform, the frame
  // stays put. Drag moves the image (imageOffset) behind the fixed frame.
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedFaceIndex, setSelectedFaceIndex] = useState(0);

  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
  // Keep containerSize in sync with the live DOM dimensions. The radix
  // Dialog animates in, so img.onload may fire before the container has its
  // final size — a ResizeObserver guarantees we converge after layout.
  useEffect(() => {
    if (!open) return;
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w && h) setContainerSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Also poll for a few frames in case the dialog is still animating.
    const timers = [50, 150, 300, 500].map((t) => setTimeout(update, t));
    return () => {
      ro.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [open]);

  // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
  // Helper: derive the fixed canvas-space crop frame from the supplied
  // dimensions. Used both inside drawCanvas (live container) and at save
  // time (state-backed containerSize) so the two paths can never disagree.
  const deriveCropRegion = useCallback((cw, ch) => {
    if (!cw || !ch) return null;
    const size = Math.min(cw, ch) * 0.6;
    return {
      x: (cw - size) / 2,
      y: (ch - size) / 2,
      width: size,
      height: size,
    };
  }, []);

  // Mirror for non-render consumers (handleSave / handleMouseDown).
  const cropRegion = useMemo(
    () => deriveCropRegion(containerSize.width, containerSize.height),
    [containerSize.width, containerSize.height, deriveCropRegion],
  );

  // Helper: compute the live image transform.
  // baseScale = "fit image fully inside the canvas".
  // scale     = baseScale × zoom%. Image is centered, then translated by imageOffset.
  const getImageTransform = useCallback(() => {
    const img = imageRef.current;
    const cw = containerSize.width || containerRef.current?.clientWidth || 0;
    const ch = containerSize.height || containerRef.current?.clientHeight || 0;
    if (!img || !cw || !ch) {
      return { scale: 1, drawW: 0, drawH: 0, offX: 0, offY: 0, cw, ch };
    }
    const baseScale = Math.min(cw / img.width, ch / img.height);
    const scale = baseScale * (zoom / 100);
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const offX = (cw - drawW) / 2 + imageOffset.x;
    const offY = (ch - drawH) / 2 + imageOffset.y;
    return { scale, drawW, drawH, offX, offY, cw, ch };
  }, [containerSize.width, containerSize.height, imageOffset.x, imageOffset.y, zoom]);

  // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
  // (cropRegion is derived via useMemo above — no init effect needed.)
  // Whenever the modal first opens or the container resizes, also re-centre
  // the image (clear any pan offset) so the user starts from a clean frame.
  useEffect(() => {
    if (!open) return;
    setImageOffset({ x: 0, y: 0 });
  }, [open, containerSize.width, containerSize.height, imageSize.width, imageSize.height]);
  
  // Load image and get dimensions
  // EF-CREW-PROFILE-PHOTO-CROP-V1: Use fetch() to load image with auth token
  useEffect(() => {
    if (imageUrl && open) {
      const loadImage = async () => {
        try {
          // Construct full URL if relative path
          const fullUrl = imageUrl.startsWith("/") ? `${API_URL}${imageUrl}` : imageUrl;
          
          // Fetch image with auth token
          const response = await fetch(fullUrl, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("eventsflo_auth_token")}`,
            },
          });
          
          if (!response.ok) {
            throw new Error(`Failed to load image: ${response.status}`);
          }
          
          // Convert to blob URL
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          
          // Load image from blob URL
          const img = new Image();
          img.onload = () => {
            setImageSize({ width: img.width, height: img.height });
            imageRef.current = img;
            // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
            // Seed containerSize from the live DOM so the canvas-space
            // crop frame can be initialised on the same tick.
            const el = containerRef.current;
            if (el) {
              setContainerSize({ width: el.clientWidth, height: el.clientHeight });
            }
            drawCanvas();
          };
          img.onerror = (e) => {
            console.error("Failed to load image from blob:", e);
          };
          img.src = blobUrl;
        } catch (error) {
          console.error("Failed to load image:", error);
        }
      };
      
      loadImage();
    }
  }, [imageUrl, open]);
  
  // Redraw canvas when any visual parameter changes (zoom or pan reuses the
  // same fixed canvas-space crop frame; rotation / face selection unaffected).
  useEffect(() => {
    if (imageRef.current) {
      drawCanvas();
    }
  }, [zoom, rotation, cropRegion, selectedFaceIndex, imageOffset.x, imageOffset.y, containerSize.width, containerSize.height]);

  // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
  // Draw canvas with image at the LIVE image transform and the crop overlay
  // in canvas-pixel space — the frame size never depends on zoom.
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    const container = containerRef.current;
    const cw = container?.clientWidth || 600;
    const ch = container?.clientHeight || 400;
    canvas.width = cw;
    canvas.height = ch;
    // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1: derive crop region
    // from the LIVE canvas dimensions (not state) so the frame is always
    // drawn — even on the very first paint before ResizeObserver fires.
    const liveCrop = deriveCropRegion(cw, ch);
    const { scale, drawW, drawH, offX, offY } = getImageTransform();

    // Background
    ctx.fillStyle = "#18181b";
    ctx.fillRect(0, 0, cw, ch);

    // Image (rotated around its own centre, translated by offset)
    ctx.save();
    ctx.translate(offX + drawW / 2, offY + drawH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();

    // Dim overlay
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, cw, ch);

    if (liveCrop) {
      // Crop frame is already in canvas pixels — draw directly.
      const cropX = liveCrop.x;
      const cropY = liveCrop.y;
      const cropW = liveCrop.width;
      const cropH = liveCrop.height;

      // Reveal the image inside the crop frame.
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillRect(cropX, cropY, cropW, cropH);
      ctx.restore();

      // Redraw image inside the clipped crop area (so the user sees the part
      // of the image currently under the fixed frame).
      ctx.save();
      ctx.beginPath();
      ctx.rect(cropX, cropY, cropW, cropH);
      ctx.clip();
      ctx.translate(offX + drawW / 2, offY + drawH / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();

      // Crop border + corner handles (canvas pixels)
      ctx.strokeStyle = "#0d9488";
      ctx.lineWidth = 2;
      ctx.strokeRect(cropX, cropY, cropW, cropH);
      const handleSize = 8;
      ctx.fillStyle = "#0d9488";
      ctx.fillRect(cropX - handleSize / 2,           cropY - handleSize / 2,           handleSize, handleSize);
      ctx.fillRect(cropX + cropW - handleSize / 2,   cropY - handleSize / 2,           handleSize, handleSize);
      ctx.fillRect(cropX - handleSize / 2,           cropY + cropH - handleSize / 2,   handleSize, handleSize);
      ctx.fillRect(cropX + cropW - handleSize / 2,   cropY + cropH - handleSize / 2,   handleSize, handleSize);
    }

    // Face bounding boxes — convert image-space → canvas-space using the
    // same live transform.
    allFaces.forEach((face, index) => {
      const faceX = offX + face.x * scale;
      const faceY = offY + face.y * scale;
      const faceW = face.width * scale;
      const faceH = face.height * scale;
      ctx.strokeStyle = index === selectedFaceIndex ? "#f59e0b" : "#6b7280";
      ctx.lineWidth = index === selectedFaceIndex ? 2 : 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(faceX, faceY, faceW, faceH);
      ctx.setLineDash([]);
      if (allFaces.length > 1) {
        ctx.fillStyle = index === selectedFaceIndex ? "#f59e0b" : "#6b7280";
        ctx.font = "12px sans-serif";
        ctx.fillText(`Face ${index + 1}`, faceX, faceY - 5);
      }
    });
  }, [getImageTransform, deriveCropRegion, rotation, allFaces, selectedFaceIndex]);
  
  // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
  // Mouse handlers now PAN the image behind the fixed crop frame (in canvas
  // pixels). Zoom is unaffected. Crop frame is invariant under drag.
  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    setIsDragging(true);
    setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const dx = x - dragStart.x;
    const dy = y - dragStart.y;
    setImageOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    setDragStart({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
  // Selecting a detected face — pan the image so the face is centered under
  // the fixed canvas crop frame. (Crop frame size is not changed.)
  const selectFace = (index) => {
    setSelectedFaceIndex(index);
    const face = allFaces[index];
    if (!face || !cropRegion) return;
    const { scale, cw, ch } = getImageTransform();
    const img = imageRef.current;
    if (!img) return;
    // Where would the face center land at current scale + zero-offset?
    const baseFaceCx = (cw - img.width * scale) / 2 + (face.x + face.width / 2) * scale;
    const baseFaceCy = (ch - img.height * scale) / 2 + (face.y + face.height / 2) * scale;
    // Required offset = crop center − unshifted face center.
    const cropCx = cropRegion.x + cropRegion.width / 2;
    const cropCy = cropRegion.y + cropRegion.height / 2;
    setImageOffset({ x: cropCx - baseFaceCx, y: cropCy - baseFaceCy });
  };
  
  // Zoom handlers
  const handleZoomIn = () => setZoom(z => Math.min(z + 10, 200));
  const handleZoomOut = () => setZoom(z => Math.max(z - 10, 50));
  
  // Rotation handlers
  const handleRotateCW = () => setRotation(r => (r + 90) % 360);
  const handleRotateCCW = () => setRotation(r => (r - 90 + 360) % 360);
  
  // Reset to defaults
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
    // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1: also reset pan.
    // (cropRegion is derived from containerSize — no setter needed.)
    setImageOffset({ x: 0, y: 0 });
  };

  // Save profile photo
  const handleSave = async () => {
    if (!cropRegion || !staffId) {
      toast.error("Invalid crop region or crew member ID");
      return;
    }
    // EVENTSFLO-PROFILE-PHOTO-ID-DOC-CROP-ZOOM-FIX-V1
    // Convert the canvas-pixel crop frame → image-pixel coordinates using
    // the current image transform, then clamp to image bounds. The result
    // is what the user actually sees inside the fixed crop frame.
    const img = imageRef.current;
    const { scale, offX, offY } = getImageTransform();
    if (!img || !scale) {
      toast.error("Image is not ready yet");
      return;
    }
    let imgX = (cropRegion.x - offX) / scale;
    let imgY = (cropRegion.y - offY) / scale;
    let imgW = cropRegion.width / scale;
    let imgH = cropRegion.height / scale;
    // Clamp to image bounds (the user can pan the frame partially off-image
    // when zoomed-out — we crop only what overlaps the real pixels).
    const clampedX = Math.max(0, Math.min(img.width - 1, imgX));
    const clampedY = Math.max(0, Math.min(img.height - 1, imgY));
    const clampedW = Math.max(1, Math.min(img.width - clampedX, imgW - (clampedX - imgX)));
    const clampedH = Math.max(1, Math.min(img.height - clampedY, imgH - (clampedY - imgY)));

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("staff_id", staffId);
      formData.append("crop_x", Math.round(clampedX).toString());
      formData.append("crop_y", Math.round(clampedY).toString());
      formData.append("crop_width", Math.round(clampedW).toString());
      formData.append("crop_height", Math.round(clampedH).toString());

      const res = await fetch(`${API_URL}/api/crew-documents/${assetId}/extract-profile-photo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("eventsflo_auth_token")}`,
        },
        body: formData,
      });

      if (!res.ok) {
        let errorData = null;
        try { errorData = await res.json(); } catch { /* ignore */ }
        throw new Error(errorData?.detail || "Failed to save profile photo");
      }

      const data = await res.json();
      toast.success("Profile photo saved successfully");

      if (onSaveComplete) {
        onSaveComplete(data);
      }
      onOpenChange(false);
    } catch (err) {
      console.error("Save profile photo error:", err);
      toast.error(err.message || "Failed to save profile photo");
    } finally {
      setSaving(false);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setZoom(100);
      setRotation(0);
      setSelectedFaceIndex(0);
      setImageOffset({ x: 0, y: 0 });
    }
  }, [open]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-card border-white/10 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="profile-photo-crop-modal"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <Camera className="h-5 w-5 text-primary" />
            Crop Profile Photo
            {allFaces.length > 1 && (
              <Badge variant="outline" className="text-amber-400 border-amber-500/30">
                {allFaces.length} faces detected
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 flex flex-col gap-4 p-4 overflow-hidden">
          {/* Face Selection (if multiple) */}
          {allFaces.length > 1 && (
            <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded">
              <span className="text-sm text-amber-300">Multiple faces detected. Select the correct portrait:</span>
              <div className="flex gap-2">
                {allFaces.map((_, index) => (
                  <Button
                    key={index}
                    variant={selectedFaceIndex === index ? "default" : "outline"}
                    size="sm"
                    onClick={() => selectFace(index)}
                    data-testid={`select-face-${index}`}
                  >
                    Face {index + 1}
                  </Button>
                ))}
              </div>
            </div>
          )}
          
          {/* Canvas Container */}
          <div 
            ref={containerRef}
            className="flex-1 min-h-[400px] bg-zinc-900 rounded-lg overflow-hidden cursor-move"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <canvas 
              ref={canvasRef} 
              className="w-full h-full"
              data-testid="crop-canvas"
            />
          </div>
          
          {/* Controls */}
          <div className="flex items-center justify-between gap-4 p-3 bg-surface-elevated/30 rounded-lg border border-white/5">
            {/* Zoom Controls */}
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleZoomOut} disabled={zoom <= 50}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 min-w-[120px]">
                <Slider
                  value={[zoom]}
                  onValueChange={([v]) => setZoom(v)}
                  min={50}
                  max={200}
                  step={5}
                  className="flex-1"
                />
                <span className="text-xs font-mono w-12 text-right">{zoom}%</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleZoomIn} disabled={zoom >= 200}>
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Rotation Controls */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleRotateCCW} title="Rotate Counter-Clockwise">
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleRotateCW} title="Rotate Clockwise">
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Reset */}
            <Button variant="outline" size="sm" onClick={handleReset}>
              Reset
            </Button>
          </div>
          
          {/* Instructions */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Move className="h-4 w-4" />
            <span>Drag to pan the image behind the fixed crop frame. Use zoom and rotate to adjust.</span>
          </div>
        </div>
        
        {/* Footer */}
        <DialogFooter className="shrink-0 px-6 py-4 border-t border-white/10 bg-surface-elevated/30">
          <div className="flex items-center justify-between w-full">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                Output: 400x400 + 100x100 thumbnail
              </Badge>
              <Button onClick={handleSave} disabled={saving || !cropRegion} data-testid="save-profile-photo-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Check className="h-4 w-4 mr-2" />
                Save Profile Photo
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProfilePhotoCropModal;
